import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { getApiErrorMessage, useApiGet } from '../../../utils/httpClientTs';
import { getPortalPaymentHistory } from '../../../endpoints/portalLoans';
import { formatPortalCurrency, formatPortalDate } from '../mock/store';
import { ELF_TEAL } from '../elfTheme';

const DEFAULT_PAGE_SIZE = 25;

const LoanHistory: React.FC = () => {
  const [page, setPage] = useState(0); // MUI is 0-based
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);

  const { data, isLoading, isError, error, refetch, isFetching } = useApiGet(
    ['portal-loans-history', page + 1, rowsPerPage],
    () =>
      getPortalPaymentHistory({
        page: page + 1,
        page_size: rowsPerPage,
      }),
    { retry: 1, placeholderData: keepPreviousData }
  );

  if (isLoading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: ELF_TEAL }} />
      </Box>
    );
  }

  if (isError && !data) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        {getApiErrorMessage(error) || 'Unable to load payment history.'}
      </Alert>
    );
  }

  const rows = data?.data ?? [];
  const total = data?.pagination.total_count ?? 0;

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Payment history
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Payments posted to your ELF account.
      </Typography>

      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 2 }}
                  >
                    No payments to show yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{formatPortalDate(row.date)}</TableCell>
                  <TableCell>{row.description}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell align="right">
                    {formatPortalCurrency(row.amount)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          disabled={isFetching}
        />
      </Paper>
    </Box>
  );
};

export default LoanHistory;
